import styled, { keyframes } from 'styled-components';

const shimmer = keyframes`
  0% { background-position: -400px 0; }
  100% { background-position: 400px 0; }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(4px);
  }
  to {
    opacity: 1;
    transform: none;
  }
`;

export const FadeIn = styled.div`
  animation: ${fadeIn} 0.35s ease;
`;

export const ResultsContainer = styled.div`
  padding: 0;
  background: transparent;
  font-family: 'Plus Jakarta Sans', sans-serif;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const BackLink = styled.button`
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  padding: 0;
  color: #64748b;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  transition: color 0.15s ease;

  &:hover {
    color: #006633;
  }
`;

export const TopBar = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;

  h2 {
    font-size: 24px;
    font-weight: 700;
    letter-spacing: -0.02em;
    color: #0f172a;
    margin: 0;
  }

  .description {
    font-size: 13px;
    color: #64748b;
    margin-top: 6px;
    font-weight: 500;
    max-width: 720px;
    line-height: 1.5;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

export const KpiRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  background: #ffffff;
  border: 1px solid #eef2f7;
  border-radius: 14px;
  padding: 4px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

type KpiTone = 'green' | 'orange' | 'blue' | 'neutral';
const KPI_TONE: Record<KpiTone, { bg: string; color: string }> = {
  green: { bg: '#e6f3ec', color: '#006633' },
  orange: { bg: '#fff1e0', color: '#f38725' },
  blue: { bg: '#e0ecff', color: '#1d4ed8' },
  neutral: { bg: '#f1f5f9', color: '#475569' },
};

export const KpiCell = styled.div<{ $tone?: KpiTone }>`
  padding: 16px 18px;
  border-right: 1px solid #eef2f7;
  display: flex;
  flex-direction: column;
  gap: 10px;
  border-radius: 10px;

  &:last-child {
    border-right: none;
  }

  @media (max-width: 768px) {
    border-right: none;

    &:not(:first-child) {
      border-top: 1px solid #eef2f7;
    }
  }

  .label {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    font-weight: 600;
    color: #64748b;
    letter-spacing: 0.01em;
  }

  .icon {
    width: 22px;
    height: 22px;
    border-radius: 6px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    background: ${({ $tone }) => KPI_TONE[$tone || 'neutral'].bg};
    color: ${({ $tone }) => KPI_TONE[$tone || 'neutral'].color};
  }

  .value {
    font-size: 30px;
    font-weight: 700;
    color: #0f172a;
    letter-spacing: -0.03em;
    line-height: 1;
    font-variant-numeric: tabular-nums;
  }

  .delta {
    font-size: 12px;
    color: #64748b;
    font-weight: 500;
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
`;

export const QuestionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

export const Card = styled.div`
  background: #ffffff;
  border: 1px solid #eef2f7;
  border-radius: 14px;
  padding: 18px 20px;
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

export const CardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;

  .titles {
    min-width: 0;
  }

  .titles h3 {
    margin: 0;
    font-size: 15px;
    font-weight: 700;
    color: #0f172a;
    letter-spacing: -0.02em;
    line-height: 1.35;
  }

  .titles .subtitle {
    display: block;
    font-size: 12px;
    color: #64748b;
    margin-top: 4px;
    font-weight: 500;
  }
`;

export const QuestionNumber = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 26px;
  height: 26px;
  padding: 0 8px;
  border-radius: 999px;
  background: #e6f3ec;
  color: #006633;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: -0.01em;
  margin-right: 10px;
  flex-shrink: 0;
`;

export const InsightPill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #e6f3ec;
  color: #006633;
  font-size: 11px;
  font-weight: 700;
  padding: 4px 10px;
  border-radius: 999px;
  letter-spacing: -0.01em;
  white-space: nowrap;

  svg {
    flex-shrink: 0;
  }
`;

export const DonutLayout = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

export const DonutCenter = styled.div`
  position: relative;
  width: 100%;

  .center {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    pointer-events: none;
  }

  .center .big {
    font-size: 24px;
    font-weight: 700;
    color: #0f172a;
    letter-spacing: -0.03em;
  }

  .center .small {
    font-size: 10.5px;
    color: #64748b;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }
`;

export const LegendList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const LegendItem = styled.li`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;

  .dot {
    width: 8px;
    height: 8px;
    border-radius: 2px;
    flex-shrink: 0;
  }

  .name {
    flex: 1;
    color: #0f172a;
    font-weight: 500;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .value {
    color: #64748b;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
  }

  .percent {
    color: #94a3b8;
    font-size: 11px;
    font-variant-numeric: tabular-nums;
    min-width: 38px;
    text-align: right;
  }
`;

export const HighlightRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 10px 12px;
  background: #f8fafc;
  border: 1px solid #eef2f7;
  border-radius: 10px;
  margin-top: 14px;

  .label {
    font-size: 11px;
    color: #64748b;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  .content {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    font-weight: 700;
    color: #0f172a;

    .dot {
      width: 10px;
      height: 10px;
      border-radius: 3px;
    }

    .pct {
      color: #64748b;
      font-weight: 600;
      font-size: 12px;
    }
  }
`;

export const EmptyState = styled.div`
  text-align: center;
  padding: 32px 0;
  color: #94a3b8;
  font-size: 12px;
  font-weight: 500;
`;

export const Skeleton = styled.div<{
  $w?: string | number;
  $h?: string | number;
  $radius?: number;
  $mb?: number;
}>`
  display: block;
  width: ${({ $w }) => (typeof $w === 'number' ? `${$w}px` : $w || '100%')};
  height: ${({ $h }) => (typeof $h === 'number' ? `${$h}px` : $h || '14px')};
  border-radius: ${({ $radius }) => $radius ?? 6}px;
  margin-bottom: ${({ $mb }) => $mb ?? 0}px;
  background: linear-gradient(90deg, #f1f5f9 0%, #e2e8f0 50%, #f1f5f9 100%);
  background-size: 800px 100%;
  animation: ${shimmer} 1.2s ease-in-out infinite;
`;

export const SkeletonCircle = styled.div<{ $size?: number }>`
  width: ${({ $size }) => $size ?? 120}px;
  height: ${({ $size }) => $size ?? 120}px;
  border-radius: 50%;
  background: linear-gradient(90deg, #f1f5f9 0%, #e2e8f0 50%, #f1f5f9 100%);
  background-size: 800px 100%;
  animation: ${shimmer} 1.2s ease-in-out infinite;
  margin: 0 auto;
`;

export const SkeletonStack = styled.div<{ $gap?: number }>`
  display: flex;
  flex-direction: column;
  gap: ${({ $gap }) => $gap ?? 10}px;
`;

export const SkeletonRowSpread = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;
